// CRM E-IV: einen gehaltenen Termin festhalten.
//
// Ein Termin in der VERGANGENHEIT ist eine Tatsache und gehört nach `Activities`; ein
// Termin in der Zukunft ist eine Absicht und gehört als `Tasks` mit
// `taskType: "meeting"` in die Aufgabenliste (G3). Deshalb hat dieses Formular keinen
// Umschalter, sondern eine Sperre: ein Datum in der Zukunft wird abgewiesen, mit dem
// Hinweis, wo es stattdessen hingehört.
//
// Aus einer offenen Aufgabe heraus symmetrisch zum Anruf in E-I: Firma, Kontakt,
// Betreff und Anfrage kommen aus der Aufgabe, `Activities.taskID` und
// `Tasks.activityIDs` werden beidseitig geschrieben, die Aufgabe geht auf erledigt und
// bleibt erhalten. Entsteht daraus eine Folge-Aufgabe, traegt die
// `originActivityID` — dieser Termin ist dann ihr Anlass.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";

import { logActivity } from "../helpers/activities";
import ActivityDocumentsField from "./ActivityDocumentsField";
import CompanySearch from "./CompanySearch";
import RichNoteField, { noteValue } from "./RichNoteField";
import { plainToHtml } from "./emailHtml";
import { saveActivityDocuments } from "./activityDocuments";
import { client } from "../helpers/amplifyClient";
import FollowUpTaskField from "./FollowUpTaskField";
import { calculateFollowUpDate } from "./taskDateHelpers";
import {
  createTasks,
  customListContactPersons,
  companiesContactPersonsByCompanyID,
} from "./graphql/taskForm.queries";

// <input type="datetime-local"> erwartet lokale Zeit ohne Zone, DynamoDB speichert
// ISO-8601 (CLAUDE.md). Die Umrechnung passiert an genau diesen zwei Stellen.
const toLocalInput = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

// Identitaet stabil halten: als Dep eines Effekts waere ein frisches [] je Rendern
// eine Endlosschleife.
const NO_CONTACTS = [];

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1 h (60 min)" },
  { value: "90", label: "1.5 h (90 min)" },
  { value: "120", label: "2 h (120 min)" },
  { value: "custom", label: "Custom" },
];

/**
 * @param company          { id, name } — die Firma des Termins
 * @param contacts         Ansprechpartner der Firma zur Auswahl, optional
 * @param contactPersonId  Vorbelegte Kontaktperson-ID, optional
 * @param task             Aufgabe, aus der der Termin entsteht, optional
 * @param objectId         Vorgang, an dem der Termin haengt (z. B. die Kundenanfrage,
 *                         aus deren Verlauf er festgehalten wird), optional
 * @param objectType       Typ dazu, Standard "CustomerRequest", optional
 * @param onClose          schließt das Modal
 * @param onSaved          Rückmeldung mit der angelegten Aktivität
 */
const ActivityFormModal = (props) => {
  const { company: companyProp, task, onClose, onSaved } = props;
  const { t } = useTranslation(["components", "common", "tasks"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));

  // Die Firma steht jetzt IM Fenster statt in einem Auswahlschritt davor: vorbelegt
  // mit der Firma der Seite, aenderbar, aber nicht leerbar — ein Termin ohne Firma
  // haengt an nichts.
  const [company, setCompany] = useState(
    companyProp ||
      (task?.companyID ? { id: task.companyID, name: task.company?.name } : null)
  );
  // Die mitgelieferten Kontakte gelten nur fuer die mitgelieferte Firma; nach einem
  // Wechsel laedt der Effekt unten die des neuen Ziels.
  const sameCompany = !companyProp?.id || company?.id === companyProp.id;
  const contacts = sameCompany ? props.contacts || NO_CONTACTS : NO_CONTACTS;

  // Der Vorgang: der ausdrueckliche Prop gewinnt, sonst der der Aufgabe. Dieselbe
  // Reihenfolge wie im Anruf-Fenster (CallOutcomePanel) und in der Notiz.
  //
  // Ohne diese beiden Zeilen fehlte einem Termin, der im Verlauf EINER ANFRAGE
  // festgehalten wird, die Zuordnung — der Verlauf der Anfrage behaelt nur, was
  // `objectID === objectId` traegt, der Termin waere dort nie wieder aufgetaucht.
  //
  // Nach einem Firmenwechsel im Fenster faellt er weg: die Anfrage gehoert der
  // vorigen Firma.
  const objectLink = !sameCompany
    ? {}
    : props.objectId
      ? { objectID: props.objectId, objectType: props.objectType || "CustomerRequest" }
      : task?.objectID
        ? { objectID: task.objectID, objectType: task.objectType }
        : {};

  const [subject, setSubject] = useState(task?.subject || "");
  const [body, setBody] = useState("");
  const [occurredAt, setOccurredAt] = useState(toLocalInput(new Date()));
  const [durationValue, setDurationValue] = useState("30");
  const [customMinutes, setCustomMinutes] = useState("30");
  const [location, setLocation] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [docs, setDocs] = useState([]);
  const [companyContacts, setCompanyContacts] = useState(contacts);
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Follow-up task state
  const [followUp, setFollowUp] = useState({
    enabled: false,
    amount: 3,
    unit: "business_days",
  });

  const [saving, setSaving] = useState(false);

  // Lade interne Mitarbeiter und Firmenkontakte
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPeople(true);
      try {
        const compId = company?.id || task?.companyID;
        const promises = [
          client.graphql({
            query: customListContactPersons,
            variables: { type: "cs" },
          }),
        ];
        if (compId && (!contacts || contacts.length === 0)) {
          promises.push(
            client.graphql({
              query: companiesContactPersonsByCompanyID,
              variables: { companyID: compId },
            })
          );
        }
        const results = await Promise.all(promises);
        if (cancelled) return;

        const internalList = results[0]?.data?.customListContactPersons || [];
        setInternalUsers(internalList);

        if (results[1]) {
          const fetchedContacts =
            results[1]?.data?.companiesContactPersonsByCompanyID?.items || [];
          setCompanyContacts(fetchedContacts);
        }
      } catch (err) {
        console.error("ActivityFormModal: error loading participants", err);
      } finally {
        if (!cancelled) setLoadingPeople(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company?.id, task?.companyID, contacts]);

  // Initialer Vorbelegungs-Effekt für Teilnehmer (eigener User + ggf. Kontaktperson der Aufgabe)
  useEffect(() => {
    const initial = [];

    // Aktueller Mitarbeiter (Line-Up)
    if (user) {
      const uName =
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.username ||
        user.email;
      initial.push({
        label: `${uName} (Line-Up)`,
        value: `internal-${user.id}`,
        type: "internal",
        name: uName,
        id: user.id,
        email: user.email,
      });
    }

    // Kontaktperson der Aufgabe oder des Props
    const targetContactId = task?.contactPersonId || props.contactPersonId;
    const contactList = contacts.length > 0 ? contacts : companyContacts;
    if (targetContactId && contactList.length > 0) {
      const matched = contactList.find((c) => c.id === targetContactId);
      if (matched) {
        const cName =
          [matched.firstName, matched.lastName].filter(Boolean).join(" ") ||
          matched.email ||
          matched.id;
        initial.push({
          label: `${cName}${matched.position ? ` · ${matched.position}` : ""} (${
            company?.name || t("Customer")
          })`,
          value: `contact-${matched.id}`,
          type: "contact",
          name: cName,
          id: matched.id,
          email: matched.email,
        });
      }
    }

    setAttendees(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, task?.contactPersonId, props.contactPersonId, companyContacts.length]);

  // Gruppierte Optionen für CreatableSelect
  const groupedOptions = useMemo(() => {
    const groups = [];
    const contactList = contacts.length > 0 ? contacts : companyContacts;

    if (contactList && contactList.length > 0) {
      groups.push({
        label: company?.name ? `${t("Contacts")} (${company.name})` : t("Company contacts"),
        options: contactList.map((c) => {
          const name =
            [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || c.id;
          return {
            label: `${name}${c.position ? ` · ${c.position}` : ""}`,
            value: `contact-${c.id}`,
            type: "contact",
            name,
            id: c.id,
            email: c.email,
          };
        }),
      });
    }

    if (internalUsers && internalUsers.length > 0) {
      groups.push({
        label: "Line-Up Team",
        options: internalUsers.map((u) => {
          const name =
            [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id;
          return {
            label: `${name} (Line-Up)`,
            value: `internal-${u.id}`,
            type: "internal",
            name,
            id: u.id,
            email: u.email,
          };
        }),
      });
    }

    return groups;
  }, [companyContacts, contacts, internalUsers, company?.name, t]);

  const effectiveDurationMinutes = useMemo(() => {
    if (durationValue === "custom") {
      const num = parseInt(customMinutes, 10);
      return isNaN(num) || num <= 0 ? 30 : num;
    }
    return parseInt(durationValue, 10) || 30;
  }, [durationValue, customMinutes]);

  // Berechnete Endzeit
  const calculatedEndDate = useMemo(() => {
    if (!occurredAt) return null;
    const start = new Date(occurredAt);
    if (isNaN(start.getTime())) return null;
    return new Date(start.getTime() + effectiveDurationMinutes * 60000);
  }, [occurredAt, effectiveDurationMinutes]);

  const formattedEndTime = useMemo(() => {
    if (!calculatedEndDate) return "—";
    const start = new Date(occurredAt);
    const sameDay = start.toDateString() === calculatedEndDate.toDateString();
    const timeStr = calculatedEndDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    if (sameDay) return timeStr;
    return `${calculatedEndDate.toLocaleDateString([], {
      day: "2-digit",
      month: "2-digit",
    })} ${timeStr}`;
  }, [calculatedEndDate, occurredAt]);

  const startsInFuture = occurredAt ? new Date(occurredAt).getTime() > Date.now() : false;

  const handleCompanySelect = (option) => {
    const picked = option?.data || (option?.value ? { id: option.value } : null);
    setCompany(
      picked?.id ? { id: picked.id, name: picked.name || option?.label || "" } : null
    );
    setCompanyContacts([]);
  };

  const save = async () => {
    if (!subject.trim() || startsInFuture || !company?.id) return;
    setSaving(true);
    try {
      const endsAtIso = calculatedEndDate ? calculatedEndDate.toISOString() : undefined;
      const durationSeconds = effectiveDurationMinutes * 60;

      // Primäre Kontaktperson der Aktivität (falls ein Unternehmens-Kontakt ausgewählt wurde)
      const primaryContact = attendees.find((a) => a.type === "contact");
      const targetContactPersonId =
        primaryContact?.id || task?.contactPersonId || props.contactPersonId || undefined;

      // Teilnehmerliste in Notizen formatieren
      const attendeeNames = attendees
        .map((a) => a.name || a.label || a.value)
        .filter(Boolean);
      // Die Notiz ist HTML, also ist es die Teilnehmerzeile auch: `splitMeetingBody`
      // trennt am `<br>`. Ein Zeilenumbruch waere im HTML nur Leerraum -- beim
      // Bearbeiten stuenden Teilnehmer und Notiz in einer Zeile und die Zeile waere
      // beim naechsten Speichern verloren.
      let finalBody = noteValue(body);
      if (attendeeNames.length > 0) {
        const attendeeText = plainToHtml(`${t("Attendees")}: ${attendeeNames.join(", ")}`);
        finalBody = finalBody ? `${attendeeText}<br><br>${finalBody}` : attendeeText;
      }

      const compId = company?.id || task?.companyID;
      // Erst die Dateien, dann der Datensatz: die Anhaenge stehen als Feld IN der
      // Aktivitaet (kein @hasMany), also muessen ihre IDs vor der Mutation feststehen.
      const documents = await saveActivityDocuments({
        docs,
        companyId: compId,
        country: user?.country,
      });

      const activity = await logActivity({
        userId: user?.id,
        task,
        fields: {
          activityType: "meeting",
          subject: subject.trim(),
          body: finalBody,
          occurredAt: new Date(occurredAt).toISOString(),
          ...(endsAtIso ? { endsAt: endsAtIso } : {}),
          ...(durationSeconds > 0 ? { durationSeconds } : {}),
          location: location.trim(),
          companyID: compId,
          ...(targetContactPersonId ? { contactPersonId: targetContactPersonId } : {}),
          ...objectLink,
          ...(documents.length > 0 ? { documents } : {}),
        },
      });

      // Follow-up Aufgabe anlegen (falls Checkbox aktiv)
      if (followUp.enabled) {
        try {
          const dueDate = calculateFollowUpDate(
            followUp.amount,
            followUp.unit
          );

          await client.graphql({
            query: createTasks,
            variables: {
              input: {
                subject: t("Follow-up: {{subject}}", { subject: subject.trim() }),
                taskType: "todo",
                status: "open",
                priority: "medium",
                dueDate: dueDate.toISOString(),
                assigneeType: "user",
                // Der Anlass der Folge-Aufgabe: dieser Termin (Gegenrichtung zu
                // Tasks.activityIDs).
                originActivityID: activity.id,
                ...(user?.id ? { assignedUserId: user.id } : {}),
                ...(compId ? { companyID: compId } : {}),
                ...(targetContactPersonId ? { contactPersonId: targetContactPersonId } : {}),
                ...objectLink,
              },
            },
          });
          toast.success(t("Follow-up task created."));
        } catch (taskErr) {
          console.error("Follow-up task could not be created", taskErr);
          toast.error(
            t("Meeting logged, but follow-up task could not be created.")
          );
        }
      }

      toast.success(t("Meeting logged."));
      if (typeof onSaved === "function") onSaved(activity);
      onClose();
    } catch (error) {
      console.error("Termin konnte nicht festgehalten werden", error);
      toast.error(t("Could not log the meeting."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">{t("Log a meeting")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>
            {t("Company")} <span className="text-danger">*</span>
          </Form.Label>
          <CompanySearch
            type="customerOrLead"
            allowLeads
            defaultValue={company}
            placeholder={t("Search company...", { ns: "tasks" })}
            onSelect={handleCompanySelect}
            isClearable
            showClear
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            {t("Subject")} <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("What was it about?")}
            autoFocus
          />
        </Form.Group>

        <Row className="g-2 mb-3">
          <Col xs={12} md={6}>
            <Form.Label>{t("Start time")}</Form.Label>
            <Form.Control
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              isInvalid={startsInFuture}
            />
            <Form.Control.Feedback type="invalid">
              {t(
                "A meeting in the future is a plan, not a record — create a task with type Meeting instead."
              )}
            </Form.Control.Feedback>
          </Col>
          <Col xs={12} md={6}>
            <Form.Label>{t("Duration")}</Form.Label>
            <Form.Select
              value={durationValue}
              onChange={(e) => setDurationValue(e.target.value)}
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            {durationValue === "custom" && (
              <div className="input-group mt-1">
                <Form.Control
                  type="number"
                  min="1"
                  max="1440"
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(e.target.value)}
                  placeholder={t("Minutes")}
                />
                <span className="input-group-text">{t("min")}</span>
              </div>
            )}
            <div className="text-muted small mt-1">
              <i className="mdi mdi-clock-outline me-1" />
              {t("Ends at:")} <span className="fw-semibold text-dark">{formattedEndTime}</span>
            </div>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>{t("Attendees")}</Form.Label>
          <CreatableSelect
            isMulti
            options={groupedOptions}
            value={attendees}
            onChange={(selected) => setAttendees(selected || [])}
            placeholder={t("Select or type attendees...")}
            formatCreateLabel={(input) => t("Add \"{{name}}\"", { name: input })}
            className="react-select"
            classNamePrefix="react-select"
            isLoading={loadingPeople}
            noOptionsMessage={() => t("No contacts found")}
          />
          <Form.Text className="text-muted small">
            {t("Select company contacts, Line-Up colleagues, or type custom names/emails.")}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{t("Location")}</Form.Label>
          <Form.Control
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("Place or conference link (e.g. Teams, Zoom, Office)")}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{t("Notes")}</Form.Label>
          <RichNoteField
            rows={4}
            value={body}
            onChange={setBody}
            placeholder={t("Meeting notes, discussion points, agreements...")}
          />
        </Form.Group>

        <ActivityDocumentsField
          docs={docs}
          setDocs={setDocs}
          companyId={company?.id || task?.companyID}
        />

        <FollowUpTaskField
          value={followUp}
          onChange={setFollowUp}
          id="meeting-follow-up-task-checkbox"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={save}
          disabled={saving || !subject.trim() || startsInFuture || !company?.id}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              {t("Saving...")}
            </>
          ) : (
            t("Save")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActivityFormModal;
